// src/components/ui/Ball.jsx
import React from "react";

/**
 * Lottery ball — light-theme version.
 *
 * Variants:
 *   - "normal"   neutral ball on white surfaces
 *   - "gold"     special ball — uses brand green (Mega Ball / Power Ball)
 *   - "powerball" classic red Powerball (results / picks history)
 *   - "megaball"  blue Mega Ball (results / picks history)
 *   - "hot"      stats: hot number (warm red)
 *   - "cold"     stats: cold number (blue)
 *   - "matched"  outline glow to indicate a match
 *
 * `hit` marks a ball that matched the official drawing. It never relies on
 * colour alone: a main ball gets an amber fill plus a dark ring and heavier
 * weight; a special ball keeps its own face and gets a thick amber ring.
 *
 * `ariaLabel` exposes the ball to screen readers (e.g. "52, hit").
 *
 * Sizes: "xs" | "sm" | "md" | "lg"
 *
 * Pass `onPanel` when the ball sits inside a dark navy panel (the
 * "normal" variant flips to a lighter face so it stays legible).
 */
export default function Ball({
  value,
  variant = "normal",
  size = "md",
  matched = false,
  hit = false,
  ariaLabel,
  animate = false,
  delay = 0,
  onPanel = false,
  className = "",
}) {
  const sizeClass = {
    xs: "w-[24px] h-[24px] text-[10px]",
    sm: "w-[30px] h-[30px] text-[11px]",
    md: "w-10 h-10 text-[13px]",
    lg: "w-[52px] h-[52px] text-[16px]",
  }[size];

  const variantStyles = {
    normal: onPanel
      ? {
          background: "rgba(255,255,255,0.10)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.18)",
        }
      : {
          background: "var(--surf-2)",
          color: "var(--txt)",
          border: "1px solid var(--border-2)",
        },
    gold: {
      background: "linear-gradient(135deg, #2EE56B, #4FF286)",
      color: "#0E2A18",
      border: "none",
      boxShadow: "0 4px 12px rgba(46,229,107,0.30)",
    },
    powerball: {
      background: "linear-gradient(135deg, #C8102E, #E4334F)",
      color: "#fff",
      border: "none",
      boxShadow: "0 3px 8px rgba(200,16,46,0.25)",
    },
    megaball: {
      background: "linear-gradient(135deg, #1F5FBF, #3F7FDB)",
      color: "#fff",
      border: "none",
      boxShadow: "0 3px 8px rgba(31,95,191,0.25)",
    },
    hot: {
      background: "linear-gradient(135deg, #E76F51, #F49174)",
      color: "#fff",
      border: "none",
      boxShadow: "0 4px 10px rgba(231,111,81,0.25)",
    },
    cold: {
      background: "linear-gradient(135deg, #3F7AC4, #6FA0DA)",
      color: "#fff",
      border: "none",
      boxShadow: "0 4px 10px rgba(63,122,196,0.25)",
    },
  }[variant];

  const matchedStyles = matched
    ? {
        border: "2px solid var(--teal)",
        boxShadow: "0 0 0 3px rgba(31,174,96,0.18)",
      }
    : {};

  const hitStyles = !hit
    ? {}
    : variant === "normal"
      ? {
          background: "linear-gradient(135deg, #F4B63F, #F7CB6E)",
          color: "#2A1E05",
          border: "2px solid #A8700A",
          boxShadow: "none",
          fontWeight: 800,
        }
      : {
          boxShadow: "0 0 0 2px var(--surf), 0 0 0 4px #F4B63F",
          fontWeight: 800,
        };

  const style = {
    ...variantStyles,
    ...matchedStyles,
    ...hitStyles,
    fontFamily: "var(--font-body)",
    ...(animate
      ? {
          animation: `revealBall 0.4s cubic-bezier(.175,.885,.32,1.275) ${delay}ms forwards`,
          opacity: 0,
        }
      : {}),
  };

  const display =
    typeof value === "number" || /^\d+$/.test(String(value))
      ? String(value).padStart(2, "0")
      : value;

  return (
    <div
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      className={`rounded-full inline-flex items-center justify-center font-semibold shrink-0 select-none ${sizeClass} ${className}`}
      style={style}
    >
      {display}
    </div>
  );
}

export function BallsRow({ children, justify = "start", className = "" }) {
  const justifyClass = {
    start:   "justify-start",
    center:  "justify-center",
    between: "justify-between",
  }[justify];

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${justifyClass} ${className}`}>
      {children}
    </div>
  );
}
